/**
 * Monaco theme objects for the Apex language.
 *
 * Defined as a `{ light, dark }` config so the registration code can pick
 * one with `pickThemedValue` (or the consumer can use the corresponding
 * theme name directly via `apex-light` / `apex-dark`).
 *
 * The token rules use VS Code's standard color palette so devs feel at
 * home — these are the same colors VS Code ships for built-in languages.
 */

export interface MonacoTokenRule {
  token: string;
  foreground: string;
  fontStyle?: string;
}

export interface MonacoThemeData {
  base: "vs" | "vs-dark";
  inherit: boolean;
  rules: MonacoTokenRule[];
  colors: Record<string, string>;
}

/** Light Apex theme — VS Code Light+ token palette. */
export const APEX_LIGHT_MONACO_THEME: MonacoThemeData = {
  base: "vs",
  inherit: true,
  rules: [
    { token: "keyword", foreground: "0000FF", fontStyle: "bold" },
    { token: "type", foreground: "267F99" },
    { token: "annotation", foreground: "808000", fontStyle: "italic" },
    { token: "string", foreground: "A31515" },
    { token: "number", foreground: "098658" },
    { token: "number.float", foreground: "098658" },
    { token: "comment", foreground: "008000", fontStyle: "italic" },
    { token: "identifier", foreground: "001080" },
    { token: "operator", foreground: "000000" },
  ],
  colors: {
    "editor.background": "#FFFFFF",
    "editor.foreground": "#1E1E1E",
    "editorLineNumber.foreground": "#237893",
    "editorLineNumber.activeForeground": "#0B216F",
    "editor.selectionBackground": "#ADD6FF",
    "editor.lineHighlightBackground": "#F3F3F3",
    "editorCursor.foreground": "#000000",
    "editorIndentGuide.background": "#D3D3D3",
    "editorIndentGuide.activeBackground": "#939393",
  },
};

/** Dark Apex theme — VS Code Dark+ token palette. */
export const APEX_DARK_MONACO_THEME: MonacoThemeData = {
  base: "vs-dark",
  inherit: true,
  rules: [
    { token: "keyword", foreground: "569CD6", fontStyle: "bold" },
    { token: "type", foreground: "4EC9B0" },
    { token: "annotation", foreground: "DCDCAA", fontStyle: "italic" },
    { token: "string", foreground: "CE9178" },
    { token: "number", foreground: "B5CEA8" },
    { token: "number.float", foreground: "B5CEA8" },
    { token: "comment", foreground: "6A9955", fontStyle: "italic" },
    { token: "identifier", foreground: "9CDCFE" },
    { token: "operator", foreground: "D4D4D4" },
  ],
  colors: {
    "editor.background": "#1E1E1E",
    "editor.foreground": "#D4D4D4",
  },
};

/** Theme name constants — keep registration names and consumer names in sync. */
export const APEX_LIGHT_THEME_NAME = "apex-light";
export const APEX_DARK_THEME_NAME = "apex-dark";
